import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { fetchAvatarsBulk } from '../../utils/avatars';
import SteamAvatar from '../common/SteamAvatar';
import MedalIcon from './MedalIcon';
import './MedalsPage.css';

/**
 * Maps category keys to i18n label keys, resolved via t() at render time
 * (module-level constant can't call hooks).
 * @type {Record<string, string>}
 */
const CATEGORY_LABELS = {
  milestones: 'medals.catMilestones',
  combat: 'medals.catCombat',
  skill: 'medals.catSkill',
  utility: 'medals.catUtility',
};

/**
 * Medals overview page showing all achievements with first-to-unlock player,
 * larger medal images, and unlock date. Sorted by rarest first.
 */
export default function MedalsPage() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_URL}/api/achievements`)
      .then(r => r.json())
      .then(async (d) => {
        const steamIds = [];
        (d.achievements || []).forEach(a => {
          if (a.first_unlocked_by?.steamid64) {
            steamIds.push(a.first_unlocked_by.steamid64);
          }
        });
        if (steamIds.length > 0) await fetchAvatarsBulk([...new Set(steamIds)]);
        setData(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="medals-page"><div className="loading-screen"><div className="loader" /></div></div>;
  }

  if (!data) {
    return <div className="medals-page"><p>{t('medals.loadFailed')}</p></div>;
  }

  const categories = ['all', ...Object.keys(CATEGORY_LABELS)];
  const filtered = filter === 'all'
    ? data.achievements
    : data.achievements.filter(a => a.category === filter);

  return (
    <div className="medals-page">
      <h1 className="page-title">{t('nav.medals')}</h1>
      <p className="medals-subtitle">
        {t('medals.subtitle', { players: data.total_players, medals: data.achievements.length })}
      </p>

      <div className="medals-filters">
        {categories.map(cat => (
          <button
            key={cat}
            className={`medal-filter-btn ${filter === cat ? 'active' : ''}`}
            onClick={() => setFilter(cat)}
          >
            {cat === 'all' ? t('matches.all') : t(CATEGORY_LABELS[cat])}
          </button>
        ))}
      </div>

      <div className="medals-grid">
        {filtered.map(ach => {
          const rarity = data.total_players > 0
            ? Math.round((ach.unlocked_count / data.total_players) * 100)
            : 0;
          const rarityLabel = rarity === 0 ? t('medals.rarityUnlocked')
            : rarity <= 10 ? t('medals.rarityLegendary')
            : rarity <= 25 ? t('medals.rarityEpic')
            : rarity <= 50 ? t('medals.rarityRare')
            : t('medals.rarityCommon');
          const rarityClass = rarity === 0 ? 'locked'
            : rarity <= 10 ? 'legendary'
            : rarity <= 25 ? 'epic'
            : rarity <= 50 ? 'rare'
            : 'common';

          const first = ach.first_unlocked_by;

          return (
            <div key={ach.id} className={`medal-card ${rarityClass}`}>
              <div className="medal-card-content">
                <div className="medal-card-icon">
                  <MedalIcon id={ach.id} emoji={ach.icon} size={120} rarity={rarityClass} />
                </div>
                <div className="medal-card-details">
                  <div className="medal-title-row">
                    <span className="medal-name">{ach.name}</span>
                    <span className={`medal-rarity-badge ${rarityClass}`}>{rarityLabel}</span>
                  </div>
                  <div className="medal-desc">{ach.description}</div>
                  <div className="medal-stats-row">
                    <span className="medal-count">
                      {t('medals.unlockedCount', { count: ach.unlocked_count, total: data.total_players })}
                    </span>
                  </div>

                  {first ? (
                    <div className="medal-first-unlock">
                      <span className="medal-first-label">{t('medals.firstUnlock')}</span>
                      <button
                        className="medal-first-player"
                        onClick={() => navigate(`/stats/players/${first.steamid64}`)}
                        title={first.player_name}
                      >
                        <SteamAvatar steamid64={first.steamid64} size={28} />
                        <span className="medal-first-name">{first.player_name}</span>
                        {first.date && (
                          <span className="medal-first-date">{first.date}</span>
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="medal-no-one">{t('medals.noneUnlocked')}</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
